/* MicroCFOWorkspace — rich client workspace for Micro CFO clients.
 * Mounts the SAME CCFS section components (MCAMasterDataPanel,
 * FilingIndexSection, DirectorOtherCompanies) with apiBase="/clients".
 *
 * Route: /admin/clients/:id/workspace
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Edit, Upload, Loader2, Lock, Landmark, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { clientsAPI } from '@/lib/api';
import { CollapsibleSection } from '@/pages/admin/ccfs/components/CollapsibleSection';
import MCAMasterDataPanel from '@/pages/admin/ccfs/MCAMasterDataPanel';
import { FilingIndexSection } from '@/pages/admin/ccfs/sections/FilingIndexSection';
import { DirectorDINCell } from '@/pages/admin/AdminCCFSLeads';
import EventsCompliancePanel from './EventsCompliancePanel';
import CapTablePanel from './CapTablePanel';

// DirectorDINCell wrapped so it always passes apiBase="/clients"
function MicroCFODinStatusCell(props) {
  return <DirectorDINCell {...props} apiBase="/clients" />;
}

export default function MicroCFOWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingMca, setUploadingMca] = useState(false);
  const [activeSection, setActiveSection] = useState('mca');

  const fetchClient = async () => {
    try {
      const { data } = await clientsAPI.getOne(id);
      setClient(data);
    } catch (e) {
      toast.error('Failed to load client');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
  }, [id]);

  const handleMcaUpload = async (file) => {
    if (!file) return;
    setUploadingMca(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ratnatraya_token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/clients/upload-mca-and-create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      toast.success(data.message);
      // If a different client id was returned (CIN already existed), navigate to it
      if (data.id && data.id !== id) {
        navigate(`/admin/clients/${data.id}/workspace`);
        return;
      }
      await fetchClient();
    } catch (e) {
      toast.error(e.message || 'MCA upload failed');
    } finally {
      setUploadingMca(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!client) {
    return (
      <div className="p-6">
        <p className="text-slate-600">Client not found.</p>
        <Link to="/admin/clients" className="text-[#1e3a5f] underline">← Back to clients</Link>
      </div>
    );
  }

  const hasMCA = !!client.mca_company_info;
  const cinUpper = (client.cin || client.mca_company_info?.cin || '').toString().toUpperCase();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin/clients" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2" data-testid="back-to-clients">
            <ArrowLeft className="w-3 h-3" /> Back to Clients
          </Link>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="workspace-entity-name">
            {client.entity_name || 'Untitled Client'}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {cinUpper && (
              <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                {cinUpper}
              </span>
            )}
            {client.is_micro_cfo && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 border border-teal-300">
                Micro CFO Retainer
              </span>
            )}
            {client.mca_company_info?.company_status && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                {client.mca_company_info.company_status}
              </span>
            )}
            {client.cin_info?.company_type_short && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                {client.cin_info.company_type_short}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/admin/clients/${id}/edit`)} data-testid="edit-client-btn">
            <Edit className="w-4 h-4 mr-1.5" /> Edit Basic Info
          </Button>
        </div>
      </div>

      {/* ─── MCA MASTER DATA ─── */}
      <CollapsibleSection
        icon={Landmark}
        title="MCA Master Data"
        borderColor="border-slate-200"
        iconColor="text-slate-500"
        bgHover="hover:bg-slate-50/50"
        chevronBg="bg-slate-200"
        testId={`mca-section-${client.id}`}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        defaultOpen={hasMCA && !client.filing_index?.total_count}
        folderUrl={client.documents_folder_url}
        badge={hasMCA ? <>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">CIN Verified</span>
          {client.mca_company_info?.company_status && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{client.mca_company_info.company_status}</span>}
          {client.cin_info?.company_type_short && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">{client.cin_info.company_type_short}</span>}
          {client.mca_company_info?.small_company && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Small Co</span>}
          {client.mca_directors?.length > 0 && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200">{client.mca_directors.length} Directors</span>}
          {client.mca_company_info?.date_of_balance_sheet && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">Last BS: {client.mca_company_info.date_of_balance_sheet}</span>}
        </> : null}
        headerRight={
          <div className="flex items-center gap-2">
            {client.mca_file_name && (
              <span className="h-7 text-xs gap-1 inline-flex items-center px-2 rounded border border-slate-200 bg-slate-50 text-slate-500">
                <Download className="w-3 h-3" /> {client.mca_file_name}
              </span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => { if (e.target.files[0]) { handleMcaUpload(e.target.files[0]); e.target.value = ''; } }}
              data-testid={`mca-file-input-${client.id}`}
            />
            {hasMCA ? (
              <span className="h-7 text-[10px] px-2 py-1 rounded border border-slate-200 bg-slate-50 text-slate-400 flex items-center gap-1" data-testid={`mca-locked-${client.id}`}>
                <Lock className="w-3 h-3" /> Locked
                {client.mca_uploaded_at && (
                  <span className="text-slate-400 font-normal ml-1 border-l border-slate-300 pl-1">
                    {new Date(client.mca_uploaded_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </span>
            ) : (
              <Button
                size="sm"
                className="h-7 text-xs bg-[#1e3a5f] hover:bg-[#152d4a] gap-1"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                disabled={uploadingMca}
                data-testid={`upload-mca-${client.id}`}
              >
                {uploadingMca ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload Excel
              </Button>
            )}
          </div>
        }
      >
        <MCAMasterDataPanel
          lead={client}
          onRefresh={fetchClient}
          apiBase="/clients"
          DinStatusCell={MicroCFODinStatusCell}
        />
      </CollapsibleSection>

      {/* ─── MCA FILING INDEX ─── */}
      <FilingIndexSection
        lead={client}
        onRefresh={fetchClient}
        sectionKey={`filing-index-${client.id}`}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        apiBase="/clients"
      />

      {/* ─── EVENTS & COMPLIANCE HISTORY (Module 2 — Session A) ─── */}
      <EventsCompliancePanel
        client={client}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      {/* ─── CAP TABLE (Module 2 — Session B-1) ─── */}
      <CapTablePanel
        client={client}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />
    </div>
  );
}
